import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { LuFilter } from "react-icons/lu";
import { IoMdClose } from "react-icons/io";
import "../Invoices/invoiceFilterBar.scss";
import { useFitToViewport } from "src/hooks/useFitToViewport";
import { sanitizeInput } from "src/helpers/sanitizeInput";

export type BankFilters = {
    amountMin: string;
    amountMax: string;
    dateFrom: string;
    dateTo: string;
    payer: string;
    purpose: string;
};

export const DEFAULT_BANK_FILTERS: BankFilters = {
    amountMin: "",
    amountMax: "",
    dateFrom: "",
    dateTo: "",
    payer: "",
    purpose: "",
};

export type BankStatusFilter = "all" | "auto" | "unmatched" | "reviewed";

interface Props {
    filters: BankFilters;
    onChange: (f: BankFilters) => void;
    statusTab: BankStatusFilter;
    setStatusTab: (v: BankStatusFilter) => void;
    counts: Record<BankStatusFilter, number>;
}

/** Same popover-panel pattern as InvoiceFilterBar (reuses its .inv-filter CSS classes) —
 *  a fuller filter set alongside the RosterSubBar tabs above, which only cover status. */
export default function BankFilterBar({ filters, onChange, statusTab, setStatusTab, counts }: Props) {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    useFitToViewport(panelRef, open);
    const { t } = useTranslation();

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const activeCount = [
        statusTab !== "all",
        filters.amountMin !== "",
        filters.amountMax !== "",
        filters.dateFrom !== "",
        filters.dateTo !== "",
        filters.payer !== "",
        filters.purpose !== "",
    ].filter(Boolean).length;

    const set = <K extends keyof BankFilters>(key: K, val: BankFilters[K]) =>
        onChange({ ...filters, [key]: val });

    const STATUS_OPTIONS: { value: BankStatusFilter; label: string }[] = [
        { value: "all", label: t("finapi.tabAll") },
        { value: "auto", label: t("finapi.statAutoMatched") },
        { value: "unmatched", label: t("finapi.statUnmatched") },
        { value: "reviewed", label: t("finapi.statReviewed") },
    ];

    return (
        <div className="inv-filter" ref={ref}>
            <button
                className={`inv-filter__btn${activeCount > 0 ? " inv-filter__btn--active" : ""}${open ? " inv-filter__btn--open" : ""}`}
                onClick={() => setOpen((o) => !o)}
            >
                <LuFilter />
                {t("filters.filters")}
                {activeCount > 0 && <span className="inv-filter__badge">{activeCount}</span>}
            </button>

            {open && (
                <div className="inv-filter__panel" ref={panelRef}>
                    <div className="inv-filter__panel-header">
                        <span>{t("filters.filters")}</span>
                        {activeCount > 0 && (
                            <button
                                className="inv-filter__clear"
                                onClick={() => { onChange(DEFAULT_BANK_FILTERS); setStatusTab("all"); }}
                            >
                                <IoMdClose /> {t("filters.clearAll")}
                            </button>
                        )}
                    </div>

                    <div className="inv-filter__row inv-filter__row--wrap">
                        <span className="inv-filter__label">{t("finapi.colStatus")}</span>
                        <div className="inv-filter__chips">
                            {STATUS_OPTIONS.map((opt) => (
                                <button
                                    key={opt.value}
                                    className={`inv-filter__chip${statusTab === opt.value ? " inv-filter__chip--active" : ""}`}
                                    onClick={() => setStatusTab(opt.value)}
                                >
                                    {opt.label}
                                    <span className="inv-filter__chip-count">{counts[opt.value]}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="inv-filter__row">
                        <span className="inv-filter__label">{t("finapi.colAmount")}</span>
                        <div className="inv-filter__date-range">
                            <input
                                type="number"
                                className="inv-filter__input inv-filter__input--date"
                                aria-label={t("filters.amountGte")}
                                placeholder={t("filters.amountPlaceholder")}
                                value={filters.amountMin}
                                onChange={(e) => set("amountMin", e.target.value)}
                            />
                            <span className="inv-filter__date-sep">–</span>
                            <input
                                type="number"
                                className="inv-filter__input inv-filter__input--date"
                                aria-label={t("finapi.filterAmountMax")}
                                placeholder={t("finapi.filterAmountMax")}
                                value={filters.amountMax}
                                onChange={(e) => set("amountMax", e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="inv-filter__row">
                        <span className="inv-filter__label">{t("table.created")}</span>
                        <div className="inv-filter__date-range">
                            <input
                                type="date"
                                className="inv-filter__input inv-filter__input--date"
                                aria-label={t("filters.createdFrom")}
                                value={filters.dateFrom}
                                onChange={(e) => set("dateFrom", e.target.value)}
                            />
                            <span className="inv-filter__date-sep">{t("filters.createdTo")}</span>
                            <input
                                type="date"
                                className="inv-filter__input inv-filter__input--date"
                                aria-label={t("filters.createdTo")}
                                value={filters.dateTo}
                                onChange={(e) => set("dateTo", e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="inv-filter__row">
                        <span className="inv-filter__label">{t("finapi.colCounterpart")}</span>
                        <input
                            type="text"
                            className="inv-filter__input"
                            aria-label={t("finapi.colCounterpart")}
                            placeholder={t("filters.search")}
                            value={filters.payer}
                            onChange={(e) => set("payer", sanitizeInput(e.target.value))}
                        />
                    </div>

                    <div className="inv-filter__row">
                        <span className="inv-filter__label">{t("finapi.colPurpose")}</span>
                        <input
                            type="text"
                            className="inv-filter__input"
                            aria-label={t("finapi.colPurpose")}
                            placeholder={t("filters.search")}
                            value={filters.purpose}
                            onChange={(e) => set("purpose", sanitizeInput(e.target.value))}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}
